import { Navigate } from 'react-router-dom';

import { useAppSelector } from 'redux/hooks';
import { getUserPermissions } from 'redux/slices/userData';

import { routes } from 'data/routes';

type ProtectedRouteProps = {
  permissions: string[];
  excludePermissions?: string[];
  children: React.ReactElement;
};

/**
 * Route-level permission guard. Renders children only if the current user
 * holds at least one of the required permissions AND none of
 * `excludePermissions` (e.g. CUSTOMER_MAPPER routes exclude ADMIN — admins
 * manage Customer Mapper through the dedicated admin panel, not here, even
 * on a dual-role account). Redirects to /home otherwise.
 *
 * On hard page reloads, permissions start as [''] in Redux (initial state).
 * We render children in this "loading" phase so DefaultLayout can trigger
 * the profile fetch. Once real permissions arrive, the guard evaluates.
 */
const ProtectedRoute = ({
  permissions,
  excludePermissions,
  children,
}: ProtectedRouteProps) => {
  const userPermissions = useAppSelector(getUserPermissions);

  // Initial Redux state has permissions: [''] — a single empty string.
  // Once the profile loads, it becomes a real array like ['USER', 'CATALOGUE'].
  const permissionsLoaded =
    userPermissions.length > 1 ||
    (userPermissions.length === 1 && userPermissions[0] !== '');

  // While loading — render children so DefaultLayout can fetch the profile.
  // This is safe because the backend rejects unauthorized API calls with 403.
  if (!permissionsLoaded) {
    return children;
  }

  const hasAccess = permissions.some((p) => userPermissions.includes(p));
  const isExcluded = excludePermissions?.some((p) => userPermissions.includes(p)) ?? false;

  if (!hasAccess || isExcluded) {
    return <Navigate to={routes.home} replace />;
  }

  return children;
};

export { ProtectedRoute };
